import { useEffect } from 'react';
import { Button, Popconfirm, Segmented, Select, message } from 'antd';
import { RotateCcw } from 'lucide-react';
import { ktApi, useKt } from './ktStore';
import { SCOPE } from './ktBooks';
import { COUNTER_ROLE, COUNTER_USERS, TIER, type Bill, type BookScope, type InvoiceStatus, type MatchStatus, type Tier } from './ktLogic';

export { PageHeader, Kpi, Panel } from '../CskhHub/shared';

/** Hai giao diện tách riêng: quầy chỉ có vai quầy, nội bộ và tổng hợp chỉ có các vai còn lại. Bản thật lấy từ quyền đăng nhập */
export type Portal = 'counter' | 'internal';
const PORTAL_TIERS: Record<Portal, Tier[]> = { counter: ['counter'], internal: ['revenue', 'chief'] };

/** Thanh demo: nói rõ dữ liệu mẫu và cho đổi vai trong giao diện đang mở */
export function DemoBar({ portal = 'internal' }: { portal?: Portal }) {
  const s = useKt();
  const tiers = PORTAL_TIERS[portal];
  useEffect(() => { if (!PORTAL_TIERS[portal].includes(s.tier)) ktApi.setTier(PORTAL_TIERS[portal][0]); }, [portal, s.tier]);
  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
      <div className="text-xs text-amber-800">
        <b>Bản demo:</b> đọc đơn từ POS, dữ liệu kế toán lưu trên máy này, EasyInvoice và eTax là giả lập.{tiers.length > 1 ? ' Đổi vai để thử:' : ''}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {tiers.length > 1 && (
          <Segmented size="small" value={s.tier} onChange={v => ktApi.setTier(v as Tier)}
            options={tiers.map(t => ({ value: t, label: TIER[t].short }))} />
        )}
        <Popconfirm title="Nạp lại dữ liệu mẫu?" description="Mọi thao tác thử trên module này sẽ mất, đơn POS được đọc lại từ đầu." okText="Nạp lại" cancelText="Thôi"
          onConfirm={() => { ktApi.resetDemo(); message.success('Đã nạp lại dữ liệu mẫu'); }}>
          <Button size="small" icon={<RotateCcw size={12} />}>Dữ liệu mẫu</Button>
        </Popconfirm>
      </div>
    </div>
  );
}

/** Người đang thao tác ở quầy: nhật ký ghi đúng tên người này; bản thật lấy từ tài khoản đăng nhập */
export function CounterBar() {
  const s = useKt();
  if (s.tier !== 'counter') return null;
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-2.5 text-sm">
      <label htmlFor="kt-counter-user" className="font-medium text-gray-700 dark:text-gray-300">Người thao tác</label>
      <Select id="kt-counter-user" size="small" className="w-64" value={s.counterUser} onChange={v => ktApi.setCounterUser(v)} options={COUNTER_USERS.map(u => ({ value: u.id, label: `${u.name} · ${COUNTER_ROLE[u.role]}` }))} />
    </div>
  );
}

const Badge = ({ className, children }: { className: string; children: React.ReactNode }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${className}`}>{children}</span>
);

export const BillStatusTag = ({ bill }: { bill: Bill }) => (bill.status === 'cancelled'
  ? <Badge className="bg-rose-50 text-rose-700 border-rose-200">Đã hủy</Badge>
  : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Hợp lệ</Badge>);

const INVOICE_TAG: Record<InvoiceStatus, { label: string; cls: string }> = {
  none: { label: 'Chưa xuất', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  issued: { label: 'Đã xuất', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  needs_adjust: { label: 'Cần điều chỉnh', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  adjusted: { label: 'Đã điều chỉnh', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  replaced: { label: 'Đã thay thế', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};
export const InvoiceTag = ({ status }: { status: InvoiceStatus }) => <Badge className={INVOICE_TAG[status].cls}>{INVOICE_TAG[status].label}</Badge>;

const MATCH_CLS: Record<MatchStatus, string> = {
  matched: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amount: 'bg-amber-50 text-amber-700 border-amber-200',
  no_bill: 'bg-rose-50 text-rose-700 border-rose-200',
  dup: 'bg-rose-50 text-rose-700 border-rose-200',
};
export const MatchTag = ({ status, label }: { status: MatchStatus; label: string }) => <Badge className={MATCH_CLS[status]}>{label}</Badge>;
export const KIND_LABEL: Record<Bill['kind'], string> = { service: 'Dịch vụ', card: 'Thẻ liệu trình', deposit: 'Đặt cọc', surcharge: 'Phiếu phụ thu' };

const SCOPE_CLS: Record<BookScope, string> = {
  both: 'bg-gray-50 text-gray-600 border-gray-200',
  tax: 'bg-sky-50 text-sky-700 border-sky-200',
  mgmt: 'bg-amber-50 text-amber-700 border-amber-200',
};
export const ScopeTag = ({ scope }: { scope: BookScope }) => <Badge className={SCOPE_CLS[scope]}>{SCOPE[scope]}</Badge>;

/** Bill đến từ đâu: đơn POS (mã đơn), server, hay tình huống mẫu */
export const SourceTag = ({ bill }: { bill: Bill }) => (bill.posOrderId
  ? <span className="text-[11px] text-sky-700 whitespace-nowrap">{bill.posOrderId}</span>
  : <span className="text-[11px] text-gray-400">{bill.source === 'demo' ? 'Tình huống mẫu' : 'Lập tay'}</span>);

export const showErrors = (errors?: string[]) => (errors || []).forEach(e => message.error(e));
